import api from './api';
import publicApi from './publicApi';

// The video service is keyed by (source, external_id) rather than a meeting row,
// because it also serves the IL product whose meetings live in a different system.
export type VideoMeetingSource = 'psy_clinic' | 'clinicui';

export interface VideoSessionSummary {
  id: number;
  sequence: number;
  room_name: string;
  status: 'active' | 'ended';
  started_at: string;
  ended_at: string | null;
  patient_joined_at: string | null;
  has_transcript: boolean;
}

export interface JoinToken {
  server_url: string;
  token: string;
  room_name: string;
  identity: string;
  session_id: number;
}

export interface StartVideoSessionResponse extends JoinToken {
  join_url: string;
  sequence: number;
  resumed: boolean;
}

export interface VideoMeeting {
  id: number;
  source: VideoMeetingSource;
  external_id: string;
  join_url: string;
  therapist_display_name: string | null;
  patient_display_name: string | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
  locale: string | null;
  sessions: VideoSessionSummary[];
}

export interface StartVideoSessionParams {
  externalId: number | string;
  therapistDisplayName?: string | null;
  patientDisplayName?: string | null;
  scheduledAt?: string | null;
  durationMinutes?: number | null;
  locale?: string | null;
  source?: VideoMeetingSource;
}

export interface PatientVideoContext {
  therapist_display_name: string | null;
  scheduled_at: string | null;
  locale: string;
  /** A room is live AND the therapist is in it - the only state a patient may enter. */
  is_open: boolean;
  /** Lets the page tell "nothing has started" from "started, and they stepped out". */
  therapist_present: boolean;
  // Not time-gated: a late start or an overrun never locks anyone out. What it
  // does depend on is the therapist being in the room, so a patient is never
  // alone in one.
  message_code: 'ok' | 'not_started' | 'waiting_for_therapist';
}

export interface OpenRoom {
  session_id: number;
  source: VideoMeetingSource;
  external_id: string;
  room_name: string;
  sequence: number;
  therapist_display_name: string | null;
  patient_display_name: string | null;
  scheduled_at: string | null;
  started_at: string;
  patient_joined_at: string | null;
  join_url: string;
}

export const videoService = {
  /**
   * Start (or re-enter) a video session. Safe to call twice: while a session is
   * live the backend returns the existing one with `resumed: true`.
   */
  async startVideoSession(params: StartVideoSessionParams): Promise<StartVideoSessionResponse> {
    const response = await api.post('/video/sessions', {
      source: params.source ?? 'psy_clinic',
      external_id: String(params.externalId),
      therapist_display_name: params.therapistDisplayName ?? null,
      patient_display_name: params.patientDisplayName ?? null,
      scheduled_at: params.scheduledAt ?? null,
      duration_minutes: params.durationMinutes ?? null,
      locale: params.locale ?? null,
    });
    return response.data;
  },

  /**
   * Get or create the VideoMeeting and return its patient join link, without
   * opening a room. Use this to share a link ahead of the session.
   */
  async ensureVideoMeeting(params: StartVideoSessionParams): Promise<VideoMeeting> {
    const response = await api.post('/video/meetings', {
      source: params.source ?? 'psy_clinic',
      external_id: String(params.externalId),
      therapist_display_name: params.therapistDisplayName ?? null,
      patient_display_name: params.patientDisplayName ?? null,
      scheduled_at: params.scheduledAt ?? null,
      duration_minutes: params.durationMinutes ?? null,
      locale: params.locale ?? null,
    });
    return response.data;
  },

  /** Every live video session the caller may see. Requires the view_video_rooms privilege. */
  async listOpenRooms(): Promise<OpenRoom[]> {
    const response = await api.get('/video/rooms/open');
    return response.data;
  },

  async getVideoMeeting(
    externalId: number | string,
    source: VideoMeetingSource = 'psy_clinic',
  ): Promise<VideoMeeting> {
    const response = await api.get(`/video/meetings/${source}/${externalId}`);
    return response.data;
  },

  async refreshToken(sessionId: number): Promise<JoinToken> {
    const response = await api.post(`/video/sessions/${sessionId}/token`);
    return response.data;
  },

  async endVideoSession(sessionId: number): Promise<VideoSessionSummary> {
    const response = await api.post(`/video/sessions/${sessionId}/end`);
    return response.data;
  },

  // --- patient side: link only, no auth ---

  async getPatientContext(joinUuid: string): Promise<PatientVideoContext> {
    const response = await publicApi.get(`/public/video/${joinUuid}/context`);
    return response.data;
  },

  /**
   * Join the room on the patient's link.
   *
   * `clientId` identifies the browser, not the person: it is what makes two
   * people on one link two participants instead of one, because LiveKit allows
   * a single connection per identity - without it the second parent to open
   * the link evicts the first. `displayName` labels their tile; blank falls
   * back to the patient's own name.
   */
  async getPatientToken(
    joinUuid: string,
    who?: { clientId?: string; displayName?: string },
  ): Promise<JoinToken> {
    const response = await publicApi.post(`/public/video/${joinUuid}/token`, {
      client_id: who?.clientId,
      display_name: who?.displayName,
    });
    return response.data;
  },

  /**
   * Report that the patient is in the room and still on their own.
   *
   * This is what lets a clinician be told ten minutes before the appointment
   * about a patient who arrived long before it: by then nothing else is awake
   * to notice, because the patient is already in the room and did not need
   * anything from the server to get there. The backend applies the timing rule
   * and notifies at most once per session, so calling this on a timer is safe.
   */
  async reportWaiting(joinUuid: string): Promise<{ status: string }> {
    const response = await publicApi.post(`/public/video/${joinUuid}/waiting`);
    return response.data;
  },
};

export default videoService;
